/**
 * 포인트 관리 시스템
 * - 개별 포인트 추가 요청을 큐에 모아서 효율적으로 처리
 * - 네트워크 효율성 향상 및 서버 부하 감소
 */

import jwt_decode from 'jsonwebtoken/decode';

const POINTS_MANAGER_CONFIG = {
  BATCH_SIZE: 50, // Wellness 50개 한번에 처리 (10 → 50으로 증가)
  FLUSH_INTERVAL: 8000, // 8초마다 자동 플러시 (5초 → 8초로 증가)
  INACTIVITY_DELAY: 2000, // 2초 동안 클릭이 없으면 처리 (1초 → 2초로 증가)
  MAX_WAIT_TIME: 8000, // 최대 8초 대기 (5초 → 8초로 증가)
  EGG_SYNC_INTERVAL: 300000, // 5분마다 에그포인트 동기화
  RETRY_LIMITS: {
    MAX_RETRIES: 1, // 재시도 횟수 감소 (2 → 1)
    INDIVIDUAL_RETRIES: 2, // 개별 재시도 감소 (3 → 2)
    RETRY_DELAY: 8000, // 재시도 지연 증가 (5초 → 8초)
    INDIVIDUAL_DELAY: 200 // 개별 지연 증가 (100ms → 200ms)
  },
  SYNC_THRESHOLDS: {
    MIN_CACHE_AGE: 300000, // 5분
    POINT_DIFFERENCE_THRESHOLD: 50 // 50포인트 이상 차이
  }
};

class PointsManager {
  constructor() {
    this.queue = [];
    this.isProcessing = false;
    this.batchSize = POINTS_MANAGER_CONFIG.BATCH_SIZE;
    this.flushInterval = POINTS_MANAGER_CONFIG.FLUSH_INTERVAL;
    this.inactivityDelay = POINTS_MANAGER_CONFIG.INACTIVITY_DELAY;
    this.maxWaitTime = POINTS_MANAGER_CONFIG.MAX_WAIT_TIME;
    this.timer = null;
    this.inactivityTimer = null;
    this.lastAddTime = null;
    
    // 에그포인트 동기화 타이머
    this.eggSyncTimer = null;
    this.eggSyncInterval = POINTS_MANAGER_CONFIG.EGG_SYNC_INTERVAL;
    this.lastSyncAttempt = 0; // 마지막 동기화 시도 시간 추적
    
    // 광고 성공 추적
    this.adSuccessTracker = new Map(); // requestId -> success status
    this.pendingAdPoints = new Map(); // requestId -> point entries
    
    // 재시도 관련
    this.retryCount = 0;
    this.queueBackup = null;
    
    // 🚨 Race Condition 방지
    this.processingLock = false;
    this.retryTimeouts = new Set(); // 활성 재시도 타이머 추적
    
    // 페이지 언로드 시 남은 큐 처리
    this.setupUnloadHandler();
    
    // 에그포인트 주기적 동기화 시작
    this.startEggSyncTimer();
    
    // 🚨 앱 시작 시 긴급 백업 복구
    this.recoverEmergencyBackups();
  }

  /**
   * 광고 관련 포인트 추가 (광고 성공 시에만 실제 큐에 추가)
   * @param {number} amount - 추가할 포인트
   * @param {string} description - 포인트 획득 설명
   * @param {string} source - 포인트 소스
   * @param {string} adRequestId - 광고 요청 ID (선택사항)
   */
  addAdRelatedPoints(amount, description = 'ad_points', source = 'general', adRequestId = null) {
    const pointEntry = {
      amount,
      description,
      source,
      timestamp: Date.now(),
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      isAdRelated: true,
      adRequestId
    };

    if (adRequestId) {
      // 광고 관련 포인트는 대기 상태로 저장
      if (!this.pendingAdPoints.has(adRequestId)) {
        this.pendingAdPoints.set(adRequestId, []);
      }
      this.pendingAdPoints.get(adRequestId).push(pointEntry);
      
      
      // 광고 성공 여부 확인 후 처리
      this.checkAndProcessAdPoints(adRequestId);
    } else {
      // 광고 ID가 없으면 즉시 큐에 추가 (일반 포인트와 동일)
      return this.addToQueue(amount, description, source);
    }

    return pointEntry.id;
  }

  /**
   * 광고 성공 결과 기록
   * @param {string} adRequestId - 광고 요청 ID
   * @param {boolean} success - 광고 성공 여부
   */
  recordAdSuccess(adRequestId, success) {
    this.adSuccessTracker.set(adRequestId, success);
    
    // 대기 중인 포인트 처리
    this.checkAndProcessAdPoints(adRequestId);
  }

  /**
   * 광고 관련 포인트 처리 확인
   * @param {string} adRequestId - 광고 요청 ID
   */
  checkAndProcessAdPoints(adRequestId) {
    const hasResult = this.adSuccessTracker.has(adRequestId);
    const hasPendingPoints = this.pendingAdPoints.has(adRequestId);
    
    if (hasResult && hasPendingPoints) {
      const success = this.adSuccessTracker.get(adRequestId);
      const pendingPoints = this.pendingAdPoints.get(adRequestId);
      
      if (success) {
        // 광고 성공 시에만 실제 큐에 추가
        pendingPoints.forEach(pointEntry => {
          this.addToQueue(pointEntry.amount, pointEntry.description, pointEntry.source);
        });
      }
      
      // 처리 완료 후 정리
      this.adSuccessTracker.delete(adRequestId);
      this.pendingAdPoints.delete(adRequestId);
    }
  }

  /**
   * 포인트 추가 요청을 큐에 추가
   * @param {number} amount - 추가할 포인트
   * @param {string} description - 포인트 획득 설명
   * @param {string} source - 포인트 소스 (scholarship, timer, etc.)
   */
  addToQueue(amount, description = 'bulk_points', source = 'general') {
    const pointEntry = {
      amount,
      description,
      source,
      timestamp: Date.now(),
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };

    this.queue.push(pointEntry);
    this.lastAddTime = Date.now();


    // 🚨 중요: 교환소 사용 시 즉시 서버 동기화 (데이터 손실 방지)
    if (source === 'exchange') {
      this.forceFlush();
    } else if (this.queue.length >= this.batchSize) {
      // 일반 포인트는 배치 크기에 도달하면 즉시 처리
      this.flushQueue();
    } else {
      // 기존 타이머들 초기화
      this.clearTimers();
      
      // 1. 비활성화 타이머 설정 (사용자가 클릭을 멈추면 빠르게 처리)
      this.scheduleInactivityFlush();
      
      // 2. 백업 타이머 설정 (최대 대기 시간)
      this.scheduleFlush();
    }

    return pointEntry.id;
  }

  /**
   * 🚀 타이머들 초기화 (최적화된 버전) - 재시도 타이머 포함
   */
  clearTimers() {
    // 🎯 기존 동작 유지: 큐 관련 타이머만 정리
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
    
    // 🚨 재시도 타이머 정리
    this.retryTimeouts.forEach(timeout => {
      clearTimeout(timeout);
    });
    this.retryTimeouts.clear();
    
    // ✅ 에그포인트 타이머는 유지 (주기적 동기화 필요) - 기존 로직 보존
  }

  /**
   * 비활성화 기반 플러시 스케줄링 (사용자가 클릭을 멈췄을 때)
   */
  scheduleInactivityFlush() {
    this.inactivityTimer = setTimeout(() => {
      if (this.queue.length > 0) {
        this.flushQueue();
      }
    }, this.inactivityDelay);
  }

  /**
   * 백업 플러시 스케줄링 (최대 대기 시간)
   */
  scheduleFlush() {
    this.timer = setTimeout(() => {
      if (this.queue.length > 0) {
        this.flushQueue();
      }
    }, this.flushInterval);
  }

  /**
   * 큐의 모든 포인트를 서버에 전송 - 강화된 롤백 메커니즘 + Race Condition 방지
   */
  async flushQueue() {
    // 🚨 Race Condition 방지: 락 체크
    if (this.processingLock || this.isProcessing || this.queue.length === 0) {
      return;
    }

    // 락 설정
    this.processingLock = true;
    this.isProcessing = true;
    
    // 모든 타이머 정리
    this.clearTimers();

    const batchToProcess = [...this.queue];
    const originalQueueLength = this.queue.length;
    
    // 🚨 안전장치: 큐 백업 생성
    const queueBackup = [...batchToProcess];
    const backupTimestamp = Date.now();
    
    // 큐를 임시로 비우되 백업은 유지
    this.queue = [];

    try {
      const totalAmount = batchToProcess.reduce((sum, entry) => sum + entry.amount, 0);
      const sources = [...new Set(batchToProcess.map(entry => entry.source))];

      await this.sendPoints(batchToProcess, totalAmount, sources);

      // 성공 시에만 로컬 스토리지 업데이트
      this.updateLocalPoints(totalAmount);
      
      // 🚨 중요: 에그포인트 동기화는 큐 처리와 분리하여 독립적으로 실행
      // 큐 처리 완료 후 비동기로 실행하여 큐와 겹치지 않도록 함
      setTimeout(() => {
        this.syncEggPoints();
      }, 1000); // 1초 후 실행
      
      // 성공 시 백업 정리
      this.clearQueueBackup();

    } catch (error) {
      console.error('❌ 포인트 처리 실패:', error);
      
      // 🚨 강화된 롤백: 실패한 항목들을 큐에 복원
      this.queue = [...queueBackup, ...this.queue];
      
      // 재시도 로직 개선
      const retryCount = this.retryCount || 0;
      const maxRetries = this.getMaxRetriesForError(error);
      
      if (retryCount < maxRetries) {
        this.retryCount = retryCount + 1;
        const retryDelay = this.getRetryDelay(retryCount);
        
        // 재시도 스케줄링 (타이머 추적)
        const retryTimeout = setTimeout(() => {
          this.retryTimeouts.delete(retryTimeout);
          this.flushQueue();
        }, retryDelay);
        
        this.retryTimeouts.add(retryTimeout);
      } else {
        console.error(`❌ 최대 재시도 횟수 초과 (${maxRetries}회): 포인트 처리 중단`);
        this.retryCount = 0;
        
        // 🚨 최종 실패 시 사용자에게 알림
        this.notifyPointsFailure(queueBackup, error);
      }
    } finally {
      // 락 해제
      this.processingLock = false;
      this.isProcessing = false;
      
      // 큐에 새로운 항목이 있으면 다시 처리 (서버 부하 고려하여 지연 시간 증가)
      if (this.queue.length > 0) {
        setTimeout(() => this.flushQueue(), 2000); // 2초 후 재처리 (서버 부하 감소)
      }
    }
  }

  /**
   * 에러 타입에 따른 최대 재시도 횟수 결정
   */
  getMaxRetriesForError(error) {
    if (error.message.includes('500') || error.message.includes('Internal Server Error')) {
      return 8; // 서버 에러는 8회 재시도 (500 에러 대응 강화)
    } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
      return 1; // 인증 에러는 1회만 재시도
    } else if (error.message.includes('403') || error.message.includes('Forbidden')) {
      return 0; // 권한 에러는 재시도 안함
    } else if (error.message.includes('Network') || error.message.includes('fetch')) {
      return 5; // 네트워크 에러는 5회 재시도
    } else {
      return 2; // 기타 에러는 2회 재시도
    }
  }

  /**
   * 재시도 횟수에 따른 지연 시간 계산 (지수 백오프)
   */
  getRetryDelay(retryCount) {
    // 500 에러에 대한 더 빠른 재시도 (서버 복구 대응)
    if (retryCount <= 3) {
      return 2000; // 처음 3회는 2초 후 재시도
    } else if (retryCount <= 6) {
      return 5000; // 4-6회는 5초 후 재시도
    } else {
      return 10000; // 7-8회는 10초 후 재시도
    }
  }

  /**
   * 포인트 처리 최종 실패 시 사용자 알림
   */
  notifyPointsFailure(failedItems, error) {
    const totalAmount = failedItems.reduce((sum, item) => sum + item.amount, 0);
    
    console.error(`🚨 포인트 처리 최종 실패: ${failedItems.length}개 항목, ${totalAmount}포인트 손실 위험`);
    
    // 포인트 손실 위험 이벤트 발생
    window.dispatchEvent(new CustomEvent('pointsLossRisk', {
      detail: {
        failedItems,
        totalAmount,
        error: error.message,
        timestamp: Date.now()
      }
    }));
  }

  /**
   * 큐 백업 정리
   */
  clearQueueBackup() {
    // 성공 시 백업 데이터 정리
    if (this.queueBackup) {
      this.queueBackup = null;
    }
  }

  /**
   * 토큰에서 user_id 추출하는 헬퍼 메서드
   */
  getUserIdFromToken(token) {
    try {
      const decodedToken = jwt_decode(token);
      return decodedToken.user_id;
    } catch (error) {
      console.error('❌ 토큰에서 user_id 추출 실패:', error);
      return null;
    }
  }

  /**
   * 지연 함수 (에러 복구용)
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * 기존 API를 사용해서 포인트 전송 (순차적으로 여러 번 호출)
   */
  async sendPoints(batch, totalAmount, sources) {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('인증 토큰이 없습니다');
    }

    const results = [];
    const errors = [];

    // 배치를 합쳐서 총합으로 한 번에 전송 (더 효율적)
    try {
      const combinedDescription = this.createCombinedDescription(batch, sources);
      
      // 토큰에서 Bearer 제거 (다른 API와 동일한 방식)
      const authToken = token.startsWith('Bearer ') ? token.replace('Bearer ', '') : token;
      
      
      // 서버 전송 시작
      
      const response = await fetch('https://api.kkokk.io/api/points/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken,
          'api_key': 'kko-a1z2-xv39-82yh0-dqlo'
        },
        body: JSON.stringify({
          amount: totalAmount,
          description: combinedDescription
        }),
      });
      
      // 서버 응답 로그 제거 (성능 최적화)

      if (!response.ok) {
        // 500 에러에 대한 상세한 로깅
        if (response.status === 500) {
          console.error('❌ 서버 500 에러 상세 정보:', {
            status: response.status,
            statusText: response.statusText,
            url: response.url,
            totalAmount,
            batchSize: batch.length
          });
          
          // 🚀 500 에러 시 즉시 재시도 (서버 일시적 문제 가능성)
          await this.delay(1000);
          
          // 재시도 로직
          try {
            const retryResponse = await fetch('https://api.kkokk.io/api/points/add', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': authToken,
                'api_key': 'kko-a1z2-xv39-82yh0-dqlo'
              },
              body: JSON.stringify({
                amount: totalAmount,
                description: combinedDescription
              }),
            });
            
            if (retryResponse.ok) {
              const retryResult = await retryResponse.json();
              return {
                success: true,
                totalAmount,
                batchSize: batch.length,
                result: retryResult
              };
            }
          } catch (retryError) {
            console.error('❌ 500 에러 재시도 실패:', retryError);
          }
        }
        
        // 401 에러 (인증 실패) - 재시도 불가
        if (response.status === 401) {
          console.error('🚨 인증 실패: 재시도 불가');
          console.error('🚨 API 실패 기록');
          throw new Error(`인증 실패: ${response.status}`);
        }
        
        console.error('🚨 API 실패 기록');
        throw new Error(`포인트 추가 실패: ${response.status}`);
      }

      const result = await response.json();
      
      return {
        success: true,
        totalAmount,
        batchSize: batch.length,
        result
      };

    } catch (error) {
      console.error('❌ 통합 포인트 전송 실패, 개별 전송으로 재시도:', error);
      
      // 토큰에서 Bearer 제거 (개별 전송용)
      const authToken = token.startsWith('Bearer ') ? token.replace('Bearer ', '') : token;
      
      // 통합 전송 실패 시 개별로 재시도
      for (const entry of batch) {
        try {
          const response = await fetch('https://api.kkokk.io/api/points/add', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': authToken,
              'api_key': 'kko-a1z2-xv39-82yh0-dqlo'
            },
            body: JSON.stringify({
              amount: entry.amount,
              description: entry.description
            }),
          });

          if (!response.ok) {
            // 🚀 500 에러 시 재시도 로직
            if (response.status === 500) {
              await this.delay(1000);
              
              try {
                const retryResponse = await fetch('https://api.kkokk.io/api/points/add', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': authToken,
                    'api_key': 'kko-a1z2-xv39-82yh0-dqlo'
                  },
                  body: JSON.stringify({
                    amount: entry.amount,
                    description: entry.description
                  }),
                });
                
                if (retryResponse.ok) {
                  const retryResult = await retryResponse.json();
                  results.push({ entry, result: retryResult });
                  continue;
                }
              } catch (retryError) {
                console.error(`❌ 개별 500 에러 재시도 실패: ${entry.amount} 포인트`, retryError);
              }
            }
            
            // 401 에러 (인증 실패) - 재시도 불가
            if (response.status === 401) {
              console.error('🚨 개별 인증 실패: 재시도 불가');
              throw new Error(`인증 실패: ${response.status}`);
            }
            
            console.error(`🚨 개별 포인트 추가 실패: ${response.status}`);
            throw new Error(`개별 포인트 추가 실패: ${response.status}`);
          }

          const result = await response.json();
          results.push({ entry, result });
          
          // 개별 요청 간 지연 시간 증가 (서버 부하 방지)
          await new Promise(resolve => setTimeout(resolve, 2000)); // 1000ms → 2000ms로 증가
          
        } catch (individualError) {
          console.error(`개별 포인트 전송 실패 (${entry.amount} 포인트):`, individualError);
          
          // 🚀 에러 분류 및 처리
          if (individualError.message.includes('500')) {
            console.error(`❌ 500 에러: ${entry.amount} 포인트 전송 실패`);
            errors.push({ entry, error: individualError });
          } else if (individualError.message.includes('401')) {
            console.error('❌ 401 Unauthorized 에러: 토큰 만료 또는 유효하지 않음. 재로그인 필요.');
            errors.push({ entry, error: individualError });
          } else {
            console.error(`❌ 기타 에러: ${entry.amount} 포인트 전송 실패`, individualError);
            errors.push({ entry, error: individualError });
          }
        }
      }

      // 일부 성공한 경우에도 결과 반환 (완전 실패가 아닌 경우)
      if (results.length > 0) {
        return {
          success: true,
          totalAmount: results.reduce((sum, r) => sum + r.entry.amount, 0),
          batchSize: results.length,
          results,
          errors: errors.length > 0 ? errors : undefined,
          fallbackMode: true
        };
      }

      if (errors.length > 0) {
        throw new Error(`${errors.length}/${batch.length}개 포인트 전송 실패`);
      }

      return {
        success: true,
        totalAmount,
        batchSize: batch.length,
        results,
        fallbackMode: true
      };
    }
  }

  /**
   * 배치 항목들을 조합해서 설명 생성 (기존 형식과 일관성 보장)
   */
  createCombinedDescription(batch, sources) {
    // 단일 소스인 경우 기존 형식 유지
    if (batch.length === 1) {
      const entry = batch[0];
      return entry.description || this.getDefaultDescription(entry.source);
    }

    // 여러 소스인 경우에만 배치 형식 사용
    const sourceCounts = {};
    
    // 소스별 개수 계산
    batch.forEach(entry => {
      sourceCounts[entry.source] = (sourceCounts[entry.source] || 0) + entry.amount;
    });

    // 소스별 한글 설명 매핑
    const sourceNames = {
      'scholarship': '장학금 수확',
      'timer': '집중 타이머',
      'wellness': '휴식 지원금',
      'reward': '리워드',
      'event': '이벤트',
      'exchange': '교환소',
      'general': '포인트'
    };

    // 소스 순서를 고정하여 일관성 보장
    const orderedSources = ['scholarship', 'timer', 'wellness', 'reward', 'event', 'exchange', 'general'];
    const descriptions = [];
    
    orderedSources.forEach(source => {
      if (sourceCounts[source]) {
        const count = sourceCounts[source];
        const name = sourceNames[source] || source;
        descriptions.push(`${name}${count > 1 ? `(${count}개)` : ''}`);
      }
    });

    const result = descriptions.length > 0 
      ? descriptions.join(', ')
      : `포인트 ${batch.length}개`;
      

    return result;
  }

  /**
   * 소스별 기본 설명 반환
   */
  getDefaultDescription(source) {
    const descriptions = {
      'scholarship': '장학금 수확',
      'timer': '집중 타이머 완료',
      'wellness': '휴식 지원금',
      'reward': '리워드 획득',
      'event': '이벤트 참여',
      'exchange': '교환소 사용',
      'general': '포인트 획득'
    };
    return descriptions[source] || '포인트 획득';
  }

  /**
   * 로컬 포인트 캐시 업데이트 (옵티미스틱 업데이트)
   */
  updateLocalPoints(amount) {
    try {
      const cachedPoints = localStorage.getItem('cached_points');
      if (cachedPoints) {
        const currentPoints = parseInt(cachedPoints, 10) || 0;
        const newPoints = currentPoints + amount;
        localStorage.setItem('cached_points', newPoints.toString());
        localStorage.setItem('cached_points_timestamp', Date.now().toString());
        
      }
    } catch (error) {
      console.warn('로컬 포인트 캐시 업데이트 실패:', error);
    }
  }

  /**
   * 페이지 언로드 시 남은 큐 처리 - 강화된 버전
   */
  setupUnloadHandler() {
    const handleUnload = () => {
      if (this.queue.length > 0) {
        // 동기적으로 마지막 요청 전송 (sendBeacon 사용)
        this.sendBeaconPoints();
      }
    };

    // 🚀 다른 창으로 넘어갈 때 큐 데이터 즉시 전송
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && this.queue.length > 0) {
        // 🚨 동기적 큐 플러시 (즉시 실행, Promise 대기 없음)
        this.forceFlushSync();
        
        // 🚀 추가로 비동기 플러시도 시도 (더 확실함)
        this.forceFlush().catch((error) => {
          console.error(`❌ 다른 창 전환 시 비동기 큐 전송 실패:`, error);
        });
      }
    };

    window.addEventListener('beforeunload', handleUnload);
    window.addEventListener('pagehide', handleUnload);
    
    // 🚀 Visibility API로 백그라운드 전환 시 강력한 큐 처리
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // 🚀 추가 안전장치: 페이지 포커스 잃을 때도 처리
    window.addEventListener('blur', () => {
      if (this.queue.length > 0) {
        // 🚨 동기적 큐 플러시 (즉시 실행)
        this.forceFlushSync();
        
        // 🚀 추가로 비동기 플러시도 시도
        this.forceFlush().catch(() => {
          this.sendBeaconPoints();
        });
      }
    });
  }

  /**
   * sendBeacon을 사용한 긴급 포인트 전송 - 강화된 안전장치
   */
  sendBeaconPoints() {
    if (this.queue.length === 0) return;

    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    const totalAmount = this.queue.reduce((sum, entry) => sum + entry.amount, 0);
    const sources = [...new Set(this.queue.map(entry => entry.source))];
    const combinedDescription = this.createCombinedDescription(this.queue, sources);

    // 🚨 안전장치: 큐 백업 생성
    const queueBackup = [...this.queue];
    const backupData = {
      items: queueBackup,
      totalAmount,
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    // localStorage에 백업 저장 (복구용)
    try {
      const existingBackups = JSON.parse(localStorage.getItem('points_emergency_backups') || '[]');
      existingBackups.push(backupData);
      
      // 최근 5개의 백업만 유지
      if (existingBackups.length > 5) {
        existingBackups.shift();
      }
      
      localStorage.setItem('points_emergency_backups', JSON.stringify(existingBackups));
    } catch (error) {
      console.warn('⚠️ 긴급 백업 저장 실패:', error);
    }

    // 기존 API 형식으로 전송
    const requestBody = JSON.stringify({
      amount: totalAmount,
      description: `${combinedDescription} (긴급전송)`
    });

    try {
      const success = navigator.sendBeacon(
        'https://api.kkokk.io/api/points/add',
        new Blob([requestBody], { 
          type: 'application/json'
        })
      );

      if (success) {
        this.updateLocalPoints(totalAmount);
        this.queue = [];
        
        // 성공 시 백업 정리
        this.clearEmergencyBackups();
      } else {
        // 실패 시 큐 복원
        this.queue = [...queueBackup];
      }
    } catch (error) {
      console.error('❌ 긴급 포인트 전송 오류:', error);
      // 오류 시 큐 복원
      this.queue = [...queueBackup];
    }
  }

  /**
   * 긴급 백업 정리
   */
  clearEmergencyBackups() {
    try {
      localStorage.removeItem('points_emergency_backups');
    } catch (error) {
      console.warn('⚠️ 긴급 백업 정리 실패:', error);
    }
  }

  /**
   * 앱 시작 시 긴급 백업 복구
   */
  async recoverEmergencyBackups() {
    try {
      const backups = JSON.parse(localStorage.getItem('points_emergency_backups') || '[]');
      
      if (backups.length === 0) return;
      
      
      // 가장 최근 백업 복구
      const latestBackup = backups[backups.length - 1];
      const timeDiff = Date.now() - latestBackup.timestamp;
      
      // 24시간 이내의 백업만 복구
      if (timeDiff < 24 * 60 * 60 * 1000) {
        this.queue = [...latestBackup.items, ...this.queue];
        
        // 복구 후 백업 정리
        this.clearEmergencyBackups();
      } else {
        this.clearEmergencyBackups();
      }
    } catch (error) {
      console.error('❌ 긴급 백업 복구 실패:', error);
    }
  }

  /**
   * 강제 플러시 (즉시 처리)
   */
  forceFlush() {
    return this.flushQueue();
  }

  /**
   * 🚀 동기적 강제 플러시 (다른 창 전환 시 사용)
   * - Promise를 기다리지 않고 즉시 실행
   */
  forceFlushSync() {
    if (this.queue.length === 0) {
      return;
    }
    
    // 큐 복사
    const queueToProcess = [...this.queue];
    this.queue = []; // 큐 즉시 비우기
    
    // 동기적으로 처리 (타임아웃 없이)
    this.processQueueSync(queueToProcess);
  }

  /**
   * 🚀 동기적 큐 처리 (타임아웃 없이 즉시 실행)
   */
  processQueueSync(queue) {
    if (queue.length === 0) return;

    const token = localStorage.getItem('token');
    if (!token) {
      console.error('❌ 동기적 큐 처리 실패: 인증 토큰이 없습니다');
      return;
    }

    try {
      // 동기적으로 전송 (fetch 대신 sendBeacon 사용)
      this.sendBeaconPoints();
    } catch (error) {
      console.error('❌ 동기적 큐 처리 실패:', error);
    }
  }

  /**
   * 큐 상태 조회
   */
  getQueueStatus() {
    return {
      queueLength: this.queue.length,
      isProcessing: this.isProcessing,
      totalPendingPoints: this.queue.reduce((sum, entry) => sum + entry.amount, 0),
      lastAddTime: this.lastAddTime
    };
  }

  /**
   * 🚀 에그포인트 주기적 동기화 타이머 시작 (최적화된 버전)
   */
  startEggSyncTimer() {
    if (this.eggSyncTimer) {
      clearInterval(this.eggSyncTimer);
    }
    
    // 🚨 매우 엄격한 조건: 5분마다 에그포인트 동기화 (오류가 있을 때만)
    this.eggSyncTimer = setInterval(async () => {
      // 오류가 있을 때만 실행되는 조건부 동기화
      const token = localStorage.getItem('token');
      if (token) {
        await this.syncEggPoints();
      }
    }, this.eggSyncInterval);
    
  }

  /**
   * 에그포인트 동기화 타이머 중지
   */
  stopEggSyncTimer() {
    if (this.eggSyncTimer) {
      clearInterval(this.eggSyncTimer);
      this.eggSyncTimer = null;
    }
  }

  /**
   * 에그포인트 동기화 - 매우 엄격한 조건으로 실행 (오류가 있을 때만)
   */
  async syncEggPoints() {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return; // 조용히 스킵 (로그 제거)
      }

      const now = Date.now();
      
      // 🚨 큐 처리 중이면 동기화 스킵 (큐와 겹치지 않도록)
      if (this.isProcessing || this.processingLock) {
        return; // 조용히 스킵 (로그 제거)
      }
      
      // 🚨 매우 엄격한 조건: 큐에 포인트가 있을 때만 처리
      if (this.queue.length > 0) {
        await this.forceFlush();
        return; // 큐 처리 후 동기화는 하지 않음
      }
      
      // 🚨 매우 엄격한 조건: 마지막 동기화로부터 5분이 지나지 않았으면 스킵
      if (this.lastSyncAttempt && (now - this.lastSyncAttempt) < 300000) { // 5분
        return; // 조용히 스킵 (로그 제거)
      }
      
      // 현재 로컬 캐시 확인
      const cachedPoints = localStorage.getItem('cached_points');
      const cachedTimestamp = localStorage.getItem('cached_points_timestamp');
      
      // 🚨 매우 엄격한 조건: 캐시가 5분 이내라면 동기화 스킵
      if (cachedTimestamp && (now - parseInt(cachedTimestamp)) < 300000) { // 5분
        this.lastSyncAttempt = now; // 시도 시간만 기록
        return; // 조용히 스킵 (로그 제거)
      }
      
      // 🚨 추가 조건: 실제 오류 상황에서만 동기화 실행
      // (큐가 비어있고, 캐시가 오래되었고, 실제 동기화가 필요한 상황)
      const hasReasonToSync = (
        !cachedPoints || // 캐시가 없음
        !cachedTimestamp || // 타임스탬프가 없음
        (now - parseInt(cachedTimestamp)) > 600000 // 10분 이상 오래된 캐시
      );
      
      if (!hasReasonToSync) {
        this.lastSyncAttempt = now;
        return; // 조용히 스킵
      }
      
      // 동기화 시도 시간 기록
      this.lastSyncAttempt = now;

      // 에그포인트 조회 API 호출
      const response = await fetch('https://api.kkokk.io/api/points', {
        method: 'GET',
        headers: {
          'Authorization': token,
          'api_key': 'kko-a1z2-xv39-82yh0-dqlo'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const serverPoints = data.total_points || 0;
        const localPoints = cachedPoints ? parseInt(cachedPoints) : 0;
        
        // 🚨 중요: 로컬 큐에 대기 중인 포인트를 고려하여 동기화
        const queueStatus = this.getQueueStatus();
        const pendingPoints = queueStatus.totalPendingPoints || 0;
        const estimatedLocalPoints = localPoints + pendingPoints;
        
        // ✅ 서버 기준으로 신뢰하고 동기화 (불일치 경고 제거)
        // 서버 포인트를 신뢰하고 로컬 포인트 업데이트
        this.updateLocalPoints(serverPoints - this.currentPoints);
      } else {
        console.warn(`⚠️ 에그포인트 동기화 실패: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ 에그포인트 동기화 오류:', error);
    }
  }


  /**
   * 🚀 큐 초기화 (최적화된 버전) - Race Condition 방지 포함
   */
  clearQueue() {
    // 🚨 락 설정하여 동시 접근 방지
    this.processingLock = true;
    
    this.queue = [];
    this.clearTimers();
    this.stopEggSyncTimer(); // 에그포인트 타이머도 중지
    
    // 🎯 메모리 정리 최적화: 추가 참조 정리
    this.adSuccessTracker.clear();
    this.pendingAdPoints.clear();
    this.retryCount = 0;
    this.lastSyncAttempt = 0;
    
    // 락 해제
    this.processingLock = false;
    this.isProcessing = false;
    
  }
}

// 싱글톤 인스턴스 생성
const pointsManager = new PointsManager();

export default pointsManager;

// 편의 함수들
export const addPointsToQueue = (amount, description, source) => {
  return pointsManager.addToQueue(amount, description, source);
};

export const addAdRelatedPointsToQueue = (amount, description, source, adRequestId) => {
  return pointsManager.addAdRelatedPoints(amount, description, source, adRequestId);
};

export const recordAdSuccessResult = (adRequestId, success) => {
  return pointsManager.recordAdSuccess(adRequestId, success);
};

export const flushPointsQueue = () => {
  return pointsManager.forceFlush();
};

export const getPointsQueueStatus = () => {
  return pointsManager.getQueueStatus();
};
